import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  onSnapshot,
  QuerySnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from './firebase';
import { CatchReport } from '../types';
import { logger } from '../utils/logger';
import AsyncStorage from '@react-native-async-storage/async-storage';

const COLLECTION_NAME = 'catchReports';
const CACHE_KEY = 'catchReports_cache';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

class CatchReportService {
  private listeners: Map<string, () => void> = new Map();

  async saveCatchReport(report: Omit<CatchReport, 'id'> & { timestamp?: string }, userId: string): Promise<CatchReport> {
    try {
      const docId = `${userId}_${Date.now()}`;
      // Use provided timestamp (from photo) or current time
      const timestamp = report.timestamp || new Date().toISOString();
      
      const catchReportData: CatchReport = {
        ...report,
        id: docId,
        userId,
        timestamp,
      };

      const docRef = doc(db, COLLECTION_NAME, docId);
      await setDoc(docRef, {
        ...catchReportData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      logger.info('Catch report saved to Firebase', { reportId: docId });

      await this.updateLocalCache(userId);

      return catchReportData;
    } catch (error) {
      logger.error('Error saving catch report to Firebase', error as Error);
      throw error;
    }
  }

  async getUserCatchReports(userId: string, maxResults: number = 50): Promise<CatchReport[]> {
    try {
      const cached = await this.getCachedReports(userId);
      if (cached) {
        logger.info('Returning cached catch reports', { count: cached.length });
        return cached;
      }

      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const querySnapshot = await getDocs(q);
      const reports: CatchReport[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reports.push({
          id: data.id,
          userId: data.userId,
          timestamp: data.timestamp,
          location: data.location,
          species: data.species,
          count: data.count,
          averageSize: data.averageSize,
          conditions: data.conditions,
          method: data.method,
          depth: data.depth,
          notes: data.notes,
          weight: data.weight,
          length: data.length,
          photos: data.photos || [],
        } as CatchReport);
      });

      await this.setCachedReports(userId, reports);

      logger.info('Fetched catch reports from Firebase', { count: reports.length });
      return reports;
    } catch (error) {
      logger.error('Error fetching catch reports from Firebase', error as Error);
      
      const cached = await this.getCachedReports(userId, true);
      if (cached) {
        logger.info('Returning expired cache due to error', { count: cached.length });
        return cached;
      }
      
      throw error;
    }
  }

  listenToUserReports(userId: string, callback: (reports: CatchReport[]) => void): () => void {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q, 
      async (snapshot: QuerySnapshot<DocumentData>) => {
        const reports: CatchReport[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          reports.push({
            id: data.id,
            userId: data.userId,
            timestamp: data.timestamp,
            location: data.location,
            species: data.species,
            count: data.count,
            averageSize: data.averageSize,
            conditions: data.conditions,
            method: data.method,
            depth: data.depth,
            notes: data.notes,
            weight: data.weight,
            length: data.length,
            photos: data.photos || [],
          } as CatchReport);
        });

        await this.setCachedReports(userId, reports);
        callback(reports);
      },
      (error) => {
        logger.error('Error in catch reports listener', error);
        // If permissions error, return empty array
        if (error.code === 'permission-denied') {
          callback([]);
        }
      }
    );

    this.listeners.set(userId, unsubscribe);
    return unsubscribe;
  }

  stopListening(userId: string) {
    const unsubscribe = this.listeners.get(userId);
    if (unsubscribe) {
      unsubscribe();
      this.listeners.delete(userId);
    }
  }

  async getCatchReportsBySpecies(userId: string, species: string): Promise<CatchReport[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('species', '==', species),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reports: CatchReport[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reports.push(data as CatchReport);
      });

      return reports;
    } catch (error) {
      logger.error('Error fetching catch reports by species', error as Error);
      throw error;
    }
  }

  async getCatchReportsByDateRange(
    userId: string, 
    startDate: Date, 
    endDate: Date
  ): Promise<CatchReport[]> {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('userId', '==', userId),
        where('timestamp', '>=', startDate.toISOString()),
        where('timestamp', '<=', endDate.toISOString()),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const reports: CatchReport[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        reports.push(data as CatchReport);
      });

      return reports;
    } catch (error) {
      logger.error('Error fetching catch reports by date range', error as Error);
      throw error;
    }
  }

  private async getCachedReports(userId: string, ignoreExpiry = false): Promise<CatchReport[] | null> {
    try {
      const cacheKey = `${CACHE_KEY}_${userId}`;
      const cached = await AsyncStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
      
      if (!ignoreExpiry && isExpired) {
        await AsyncStorage.removeItem(cacheKey);
        return null;
      }
      
      return data;
    } catch (error) {
      logger.error('Error reading cache', error as Error);
      return null;
    }
  }

  private async setCachedReports(userId: string, reports: CatchReport[]): Promise<void> {
    try {
      const cacheKey = `${CACHE_KEY}_${userId}`;
      const cacheData = {
        data: reports,
        timestamp: Date.now(),
      };
      
      await AsyncStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      logger.error('Error setting cache', error as Error);
    }
  }

  private async updateLocalCache(userId: string): Promise<void> {
    try {
      const reports = await this.getUserCatchReports(userId);
      await this.setCachedReports(userId, reports);
    } catch (error) {
      logger.error('Error updating local cache', error as Error);
    }
  }

}

export const catchReportService = new CatchReportService();